/**
 * Orders concurrent update checks so the gate the LATEST check read is the one
 * that stands: a check takes a token before reading the gate and carries it
 * across every await, and once a newer token exists it may write nothing.
 */
export type UpdateCheckSequence = {
  /** Take the token for a check that is about to read the gate. */
  begin: () => number;
  /** Whether `token` still belongs to the most recent check to have started. */
  isLatest: (token: number) => boolean;
};

/**
 * An independent ordering. A surface whose checks write only its own state owns
 * one, so its in-flight check is superseded by its own later check and never
 * by a check elsewhere.
 */
export function createUpdateCheckSequence(): UpdateCheckSequence {
  let latestToken = 0;
  return {
    begin: () => {
      latestToken += 1;
      return latestToken;
    },
    isLatest: (token) => token === latestToken,
  };
}

/** The ordering shared by every check that writes the update model's state. */
const modelSequence = createUpdateCheckSequence();

/** Take the token for a check that is about to read the gate. */
export function beginUpdateCheck(): number {
  return modelSequence.begin();
}

/** Whether `token` still belongs to the most recent check to have started. */
export function isLatestUpdateCheck(token: number): boolean {
  return modelSequence.isLatest(token);
}
